import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, FileSignature, Loader2, PauseCircle } from 'lucide-react';

import PortalLayout from '../PortalLayout';
import { usePortal } from '../../../contexts/PortalContext';
import { useContinuePortalPaperwork, usePortalMe } from '../../../services/portal';
import { TILE } from '../portalShared';

// Application-status screen — the ONLY portal surface for non-active payees
// (the API enforces this server-side; this page is just the friendly face).
// Polls /me so an approval mid-session unlocks the full portal automatically.
const PortalStatus = () => {
  const navigate = useNavigate();
  const { updatePortalStatus } = usePortal();
  const me = usePortalMe({ refetchInterval: 30000 });

  useEffect(() => {
    if (me.data?.status) {
      updatePortalStatus(me.data.status);
      if (me.data.status === 'active') navigate('/portal', { replace: true });
    }
  }, [me.data?.status, updatePortalStatus, navigate]);

  const inactive = me.data?.status === 'inactive';
  // Paperwork they haven't finished, or a fix we asked for (Nic 2026-10-24):
  // they were told it was under review. Continue signing (unfinished only)
  // opens their own packet (anything typed on this device comes back).
  const paperwork = !inactive ? me.data?.paperwork || null : null;
  const cont = useContinuePortalPaperwork({
    onSuccess: (body) => {
      if (body?.signUrl) window.location.assign(body.signUrl);
    },
  });
  // Back from the signer: the browser may restore this page still "Opening…".
  useEffect(() => {
    const onShow = (e) => {
      if (!e.persisted) return;
      cont.reset();
      me.refetch();
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const opening = cont.isLoading || cont.isSuccess;

  return (
    <PortalLayout hideNav>
      <div className="flex flex-col items-center py-10">
        <div className={`${TILE} w-full max-w-md p-6 text-center sm:p-8`}>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
            {inactive ? (
              <PauseCircle className="h-7 w-7 text-neutral-300" />
            ) : paperwork ? (
              <FileSignature className="h-7 w-7 text-sky-300" />
            ) : (
              <Clock className="h-7 w-7 text-amber-300" />
            )}
          </div>
          {inactive ? (
            <>
              <h1 className="text-lg font-bold text-white">Your partner account is paused</h1>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                Your referral link isn&apos;t active right now. If you think this is a mistake, or
                you&apos;d like to get going again, reach out to us at{' '}
                <a href="mailto:support@veritasratings.com" className="text-neutral-200 underline">
                  support@veritasratings.com
                </a>
                .
              </p>
            </>
          ) : paperwork === 'needs_fix' ? (
            // A fix we asked for (QA 2026-10-26): the reviewer's note is
            // internal and Continue signing would open a fresh blank packet,
            // so say we'll reach out. A packet a reviewer re-sends arrives by
            // email, same as on the under-review screen.
            <>
              <h1 className="text-lg font-bold text-white">We need a few changes to your paperwork</h1>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                Our team is going through it now. We&apos;ll reach out with what to update.
              </p>
              <p className="mt-3 text-xs text-neutral-500">
                Asked to re-sign something? Check your email for the fresh signing link.
              </p>
            </>
          ) : paperwork ? (
            <>
              <h1 className="text-lg font-bold text-white">Finish your paperwork</h1>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                You started your partner paperwork but haven&apos;t submitted it yet. Pick up where you left off; it only takes a few minutes.
              </p>
              <button
                type="button"
                onClick={() => cont.mutate()}
                disabled={opening}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-neutral-900 outline-none hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 disabled:opacity-70"
              >
                {opening ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
                {opening ? 'Opening…' : 'Continue signing'}
              </button>
              {cont.isError ? (
                <p role="alert" className="mt-3 text-xs text-rose-300">
                  {cont.error?.response?.data?.msg || "We couldn't open your paperwork. Please try again."}
                </p>
              ) : null}
            </>
          ) : (
            <>
              <h1 className="text-lg font-bold text-white">Your application is under review</h1>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                We&apos;ve got your paperwork and our team is going through it now. Once
                you&apos;re approved you&apos;ll get an email with your personal referral link and
                QR code — and this portal will unlock automatically.
              </p>
              <p className="mt-3 text-xs text-neutral-500">
                Asked to re-sign something? Check your email for the fresh signing link.
              </p>
            </>
          )}
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalStatus;
